import { useMutation, useQuery, useReactiveVar } from "@apollo/client";
import { LocationGenerics } from "router/location";
import { ApproveServiceOrderForm } from "components/forms";
import { Modal, ConfirmationModal } from "components/layouts";
import { useFormik } from "formik";
import toast from "react-hot-toast";
import { useSearch } from "react-location";
import { APPROVE_SERVICE_ORDER, GET_SERVICE_ORDER } from "./apollo";
import { useMemo, useState } from "react";
import { currentUserVar } from "apollo/cache/auth";

export default function ApproveInspectionServiceOrderContainer({
  open,
  setOpen,
  refetch,
}: {
  open: boolean;
  setOpen: (val: boolean) => void;
  refetch?: () => void;
}) {
  const searchParams = useSearch<LocationGenerics>();
  const currentUser = useReactiveVar(currentUserVar);
  const { data, loading } = useQuery(GET_SERVICE_ORDER, {
    variables: {
      id: searchParams.id,
    },
    notifyOnNetworkStatusChange: false,
  });
  const [approveServiceOrder] = useMutation(APPROVE_SERVICE_ORDER);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

  const canPerform = useMemo(
    () => data?.serviceOrder?.assigner?._id === currentUser?._id,
    [data?.serviceOrder?.assigner?._id, currentUser?._id],
  );

  const form = useFormik({
    initialValues: {
      notes: "",
    },
    onSubmit: async (values) => {
      await approveServiceOrder({
        variables: {
          id: searchParams.id,
          ...values,
        },
      }).then(({ data }) => {
        if (data.approveServiceOrder._id) {
          toast(
            JSON.stringify({
              type: "success",
              title: "Service Order Approved Successfully",
            })
          );
          refetch?.();
          form.resetForm();
        } else {
          toast(
            JSON.stringify({
              type: "error",
              title: "Could not approve Service Order",
            })
          );
        }
      });
    },
    onReset: () => {
      setOpen(false);
    },
  });

  return (
    <>
      <Modal
        open={open}
        setOpen={setOpen}
        loading={loading}
        title="Approve Service Order"
        description="Provide notes for approving service order"
        renderActions={() => (
          <>
            {canPerform && (
              <button
                type="button"
                disabled={form.isSubmitting}
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
                onClick={() => setIsConfirmationOpen(true)}
              >
                Approve Service Order
              </button>
            )}
          </>
        )}
      >
        {data?.serviceOrder?._id && <ApproveServiceOrderForm form={form} />}
      </Modal>
      <ConfirmationModal
        open={isConfirmationOpen}
        disabled={form.isSubmitting}
        title={"Are you sure you want to approve this service order?"}
        okayText={form.isSubmitting ? "Approving..." : "Approve"}
        onCancel={() => setIsConfirmationOpen(false)}
        onOkay={form.handleSubmit}
      />
    </>
  );
}
